import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Check, Copy } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Icon } from "../icons.js";
import { Button } from "./Button.js";
/** How long the button says Copied before it says Copy again (design-spec §5 `code-copied`). */
export const COPIED_MS = 1600;
/**
 * Copies `text` with `navigator.clipboard.writeText` and says so for {@link COPIED_MS}: the
 * glyph turns to a check, the button's words (or, glyph-only, its accessible name) to
 * "Copied" (design-spec §2 "Pending code line + copy icon", §5 `code-copied`,
 * `row-code-copied`). A write only: nothing here reads the clipboard. A refused write leaves
 * it reading Copy, so it never claims a copy that did not happen, and calls `onRefused`. A press
 * while it reads Copied copies again and restarts the timer (Outlook design-spec §5.5).
 */
export function CopyButton({ text, size, label = "Copy", variant = "secondary", buttonSize = "sm", children, onRefused, ...data }) {
    const [copied, setCopied] = useState(false);
    const timer = useRef(null);
    useEffect(() => () => {
        if (timer.current !== null)
            clearTimeout(timer.current);
    }, []);
    const copy = () => {
        void navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            if (timer.current !== null)
                clearTimeout(timer.current);
            timer.current = setTimeout(() => setCopied(false), COPIED_MS);
        }, () => {
            setCopied(false);
            onRefused?.();
        });
    };
    if (size === "button" && children !== undefined) {
        // Both words share one grid cell, the hidden one holding the width (no reflow on Copied).
        const shown = { gridArea: "1 / 1", display: "inline-flex", alignItems: "center", gap: 6 };
        const hidden = { ...shown, visibility: "hidden" };
        return (_jsx(Button, { variant: variant, size: buttonSize, "data-copy": "", "data-copied": copied ? "" : undefined, ...data, onClick: copy, children: _jsxs("span", { style: { display: "inline-grid", justifyItems: "center" }, children: [_jsx("span", { "aria-hidden": copied ? true : undefined, style: copied ? hidden : shown, children: children }), _jsxs("span", { "aria-live": "polite", style: copied ? shown : hidden, children: [copied && _jsx(Icon, { icon: Check, size: 14, "aria-hidden": "true" }), copied ? "Copied" : ""] })] }) }));
    }
    if (size === "button") {
        return (_jsx(Button, { variant: "secondary", size: "sm", "data-copy": "", "data-copied": copied ? "" : undefined, ...data, onClick: copy, iconLeft: _jsx(Icon, { icon: copied ? Check : Copy, size: 16, "aria-hidden": "true" }), style: { minWidth: 96 }, children: _jsx("span", { "aria-live": "polite", children: copied ? "Copied" : "Copy" }) }));
    }
    return (_jsx("button", { type: "button", "data-copy": "", "data-copied": copied ? "" : undefined, ...data, "aria-label": copied ? "Copied" : label, onClick: copy, style: {
            width: 24, height: 24, padding: 0, margin: 0, border: "none", flex: "none",
            display: "grid", placeItems: "center", borderRadius: "var(--radius-item)",
            background: "var(--copy-bg, transparent)", color: "var(--copy-ink, var(--ink-2))",
            cursor: "pointer",
            transition: "background-color var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out)",
        }, children: _jsx(Icon, { icon: copied ? Check : Copy, size: 14, "aria-hidden": "true" }) }));
}
